import React, { useState } from 'react';
import { DEPARTEMENTS_METRO, DEPARTEMENTS_OUTREMER, FRANCE_VIEWBOX } from '../PerimetreSelector/franceDepartements';
import './CarteAffectation.css';

interface CarteAffectationProps {
  // departement_id -> id de l'affecte (interlocuteur AC / membre Libre), null si dans le
  // perimetre mais non affecte. Une cle absente = departement HORS perimetre (grise, non cliquable).
  affecteParDept: Map<number, number | null>;
  // id de l'affecte -> couleur. Sert aussi de legende (porte par le parent).
  couleurParAffecte: Map<number, string>;
  // id de l'affecte -> "Prenom Nom" (pour l'infobulle).
  nomParAffecte: Map<number, string>;
  // id de l'affecte -> coordonnees deja formatees (fonction / email / tel) pour l'infobulle. Optionnel.
  coordParAffecte?: Map<number, string>;
  selection: Set<number>;
  onToggle: (departementId: number) => void;
  // Survol d'une personne (roster) : ses departements ressortent, les autres s'attenuent.
  survoleAffecte?: number | null;
  // Recherche : si fourni, les departements hors de cet ensemble s'attenuent (surlignage).
  surligneDepts?: Set<number> | null;
  // Enregistrement en cours : voile + clic neutralise.
  saving?: boolean;
  // Lecture seule : aucun clic (apercu annuaire) ; l'infobulle reste active.
  readOnly?: boolean;
}

interface Hover {
  id: number;
  nom: string;
  affecte: string | null;
  coord: string | null;
  x: number;
  y: number;
}

/**
 * Carte de France d'AFFECTATION. Reutilise la geometrie des departements (memes ids que la
 * table departements). Chaque departement du perimetre est cliquable et colore selon son
 * affecte ; hors perimetre il est grise. La selection multiple est materialisee par un liisere.
 * Aucune logique metier ici : la verite (affectation, selection) est portee par le parent.
 */
const CarteAffectation: React.FC<CarteAffectationProps> = ({
  affecteParDept,
  couleurParAffecte,
  nomParAffecte,
  coordParAffecte,
  selection,
  onToggle,
  survoleAffecte = null,
  surligneDepts = null,
  saving = false,
  readOnly = false,
}) => {
  const [hover, setHover] = useState<Hover | null>(null);

  const classeDept = (deptId: number): string => {
    // S'attenue si hors de la personne survolee OU hors de la recherche en cours.
    const horsSurvol = survoleAffecte != null && (affecteParDept.get(deptId) ?? null) !== survoleAffecte;
    const horsRecherche = surligneDepts != null && !surligneDepts.has(deptId);
    const attenue = horsSurvol || horsRecherche ? ' is-attenue' : '';
    if (!affecteParDept.has(deptId)) return `carte-dept carte-dept--hors${attenue}`;
    const affecte = affecteParDept.get(deptId) ?? null;
    const etat = affecte != null ? 'affecte' : 'libre';
    return `carte-dept carte-dept--${etat}${selection.has(deptId) ? ' is-selected' : ''}${attenue}`;
  };

  // Couleur de remplissage inline (uniquement pour un departement affecte) ; sinon le CSS gere.
  const fillDept = (deptId: number): string | undefined => {
    const affecte = affecteParDept.get(deptId);
    return affecte != null ? couleurParAffecte.get(affecte) : undefined;
  };

  const survol = (e: React.MouseEvent, id: number, nom: string) => {
    if (!affecteParDept.has(id)) {
      setHover(null);
      return;
    }
    const affecte = affecteParDept.get(id) ?? null;
    setHover({
      id,
      nom,
      affecte: affecte != null ? (nomParAffecte.get(affecte) ?? null) : null,
      coord: affecte != null ? (coordParAffecte?.get(affecte) ?? null) : null,
      x: e.clientX,
      y: e.clientY,
    });
  };

  const clic = (deptId: number) => {
    if (!saving && !readOnly && affecteParDept.has(deptId)) onToggle(deptId);
  };

  return (
    <div className={`carte-affectation${saving ? ' is-saving' : ''}${readOnly ? ' is-readonly' : ''}`}>
      <div className="carte-affectation-canvas">
        <svg viewBox={FRANCE_VIEWBOX} className="carte-affectation-svg" role="img" aria-label="Carte d'affectation des départements">
          {DEPARTEMENTS_METRO.map((dep) => (
            <path
              key={dep.id}
              d={dep.d}
              className={classeDept(dep.id)}
              style={{ fill: fillDept(dep.id) }}
              onClick={() => clic(dep.id)}
              onMouseMove={(e) => survol(e, dep.id, dep.nom)}
              onMouseLeave={() => setHover(null)}
            />
          ))}
          {/* Calque de selection au-dessus de tous les departements : garantit que le lisere noir
              complet passe par-dessus les voisins (sinon leur trait masque la frontiere commune). */}
          {DEPARTEMENTS_METRO.filter((dep) => selection.has(dep.id)).map((dep) => (
            <path key={`sel-${dep.id}`} d={dep.d} className="carte-dept-selection" />
          ))}
        </svg>

        {saving && (
          <div className="carte-affectation-saving">
            <span className="carte-affectation-saving-pill">Enregistrement…</span>
          </div>
        )}

        {hover && !saving && (
          <div className="carte-affectation-tooltip" style={{ left: hover.x + 12, top: hover.y + 12 }}>
            <strong>{hover.id} - {hover.nom}</strong>
            <span>{hover.affecte ?? 'Non affecté'}</span>
            {hover.coord && <span className="carte-affectation-tooltip-coord">{hover.coord}</span>}
          </div>
        )}
      </div>

      {DEPARTEMENTS_OUTREMER.length > 0 && (
        <div className="carte-affectation-outremer">
          <span className="carte-affectation-outremer-label">Outre-mer</span>
          <div className="carte-affectation-dom-grid">
            {DEPARTEMENTS_OUTREMER.map((d) => (
              <button
                type="button"
                key={d.id}
                className={`carte-dom-card ${classeDept(d.id)}`}
                style={{ fill: fillDept(d.id) }}
                onClick={() => clic(d.id)}
                disabled={saving || !affecteParDept.has(d.id)}
                title={`${d.id} - ${d.nom}`}
              >
                <svg viewBox={d.viewBox} className="carte-dom-svg" aria-hidden="true">
                  <path d={d.d} className={classeDept(d.id)} style={{ fill: fillDept(d.id) }} />
                </svg>
                <span className="carte-dom-name">{d.id} - {d.nom}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CarteAffectation;
